import jsPDF from "jspdf";
import { registerHelveticaFont } from "./registerHelveticaFont";

/**
 * Regular NIN Slip PDF Generator
 *
 * This utility generates PDF slips for regular NIN verification results.
 * It uses a portrait A4 format (210mm x 297mm) with the template image centered on the page.
 * The template maintains its aspect ratio and is scaled to fit within 80% of the page.
 * User data is overlaid on top of the template at precise positions matching the sample layout.
 * Uses real Helvetica font (loaded from TTF file) for authentic rendering without italic issues.
 *
 * Future slip types (Premium, Standard, Advanced) will have their own generators.
 */

interface RegularNINVerificationData {
  nin: string;
  lastName: string;
  firstName: string;
  middleName: string;
  gender: string;
  trackingId?: string;
  residenceAddress?: string;
  image?: string;
}

export const generateRegularNINSlipPDF = async (
  data: RegularNINVerificationData
): Promise<void> => {
  // Use portrait A4 format
  const doc = new jsPDF({
    orientation: "portrait", // A4 portrait (210mm x 297mm)
    unit: "mm",
    format: "a4",
  });

  // Register the real Helvetica font
  const fontRegistered = await registerHelveticaFont(doc);
  const fontFamily = fontRegistered ? "Helvetica-Regular" : "helvetica";

  console.log(
    `Font registration: ${
      fontRegistered ? "Success" : "Failed"
    }, using: ${fontFamily}`
  );

  // A4 Portrait dimensions
  const pageWidth = 210; // A4 portrait width
  const pageHeight = 297; // A4 portrait height

  try {
    // Load the template image
    const templateImage = await loadImage("/regular.jpg");

    // Get image dimensions to calculate aspect ratio
    const img = new Image();
    img.src = templateImage;

    await new Promise((resolve) => {
      img.onload = resolve;
    });

    // Calculate template dimensions maintaining aspect ratio
    const aspectRatio = img.width / img.height;
    let templateWidth, templateHeight;

    // Fit template within A4 page while maintaining aspect ratio
    if (aspectRatio > pageWidth / pageHeight) {
      // Image is wider than page ratio - fit to width
      templateWidth = pageWidth * 0.8; // Use 80% of page width
      templateHeight = templateWidth / aspectRatio;
    } else {
      // Image is taller than page ratio - fit to height
      templateHeight = pageHeight * 0.8; // Use 80% of page height
      templateWidth = templateHeight * aspectRatio;
    }

    // Center the template on the A4 page
    const startX = (pageWidth - templateWidth) / 2;
    const startY = (pageHeight - templateHeight) / 2;

    // Add the template centered on the page
    doc.addImage(
      templateImage,
      "JPEG",
      startX,
      startY,
      templateWidth,
      templateHeight
    );

    // Calculate scaling factor based on template size
    const scaleFactor = templateWidth / 210; // Assuming template was designed for ~210mm width

    // Debug information
    console.log(
      `Template dimensions: ${templateWidth.toFixed(
        2
      )}mm x ${templateHeight.toFixed(2)}mm`
    );
    console.log(
      `Template position: (${startX.toFixed(2)}, ${startY.toFixed(2)})`
    );
    console.log(`Scale factor: ${scaleFactor.toFixed(3)}`);
    console.log(
      `Row positions: ${(startY + 30 * scaleFactor).toFixed(2)}, ${(
        startY +
        42 * scaleFactor
      ).toFixed(2)}, ${(startY + 54 * scaleFactor).toFixed(2)}, ${(
        startY +
        66 * scaleFactor
      ).toFixed(2)}`
    );

    // Set font family - using real Helvetica font
    doc.setFont(fontFamily, "normal");

    // Data positioning based on the sample screenshot
    // All positions are relative to the centered template

    // Row 1: Tracking ID, Surname, Address (reduced from 38mm to 30mm)
    const row1Y = startY + 31 * scaleFactor;

    // Tracking ID (left column)
    doc.setFontSize(9 * scaleFactor);
    doc.setFont(fontFamily, "normal");
    if (data.trackingId) {
      doc.text(data.trackingId, startX + 23 * scaleFactor, row1Y);
    }

    // Surname (middle column) - increased left spacing from 60mm to 70mm
    doc.setFont(fontFamily, "normal");
    doc.text(data.lastName.toUpperCase(), startX + 80 * scaleFactor, row1Y);

    const row1YAdress = startY + 33 * scaleFactor;

    // Address (right column) - with smart text wrapping
    if (data.residenceAddress) {
      doc.setFont(fontFamily, "normal");

      // Smart text wrapping for address
      const addressText = data.residenceAddress.toUpperCase();
      const maxWidth = 35 * scaleFactor; // Maximum width for address field
      const lineHeight = 4 * scaleFactor; // Height between lines

      const wrappedAddress = wrapTextSmart(addressText, maxWidth, doc);

      console.log(
        `Address wrapping: "${addressText}" -> ${wrappedAddress.length} lines:`,
        wrappedAddress
      );

      // Draw each line of the wrapped address
      wrappedAddress.forEach((line, index) => {
        const lineY = row1YAdress + index * lineHeight;
        doc.text(line, startX + 122 * scaleFactor, lineY);
      });
    }

    // Row 2: NIN, First Name (reduced from 50mm to 42mm)
    const row2Y = startY + 42 * scaleFactor;

    // NIN (left column)
    doc.setFont(fontFamily, "normal");
    doc.text(data.nin, startX + 20 * scaleFactor, row2Y);

    // First Name (middle column) - increased left spacing from 60mm to 80mm
    doc.setFont(fontFamily, "normal");
    doc.text(data.firstName.toUpperCase(), startX + 80 * scaleFactor, row2Y);

    // Row 3: Middle Name (reduced from 62mm to 54mm)
    const row3Y = startY + 52 * scaleFactor;
    doc.setFont(fontFamily, "normal");
    doc.text(data.middleName.toUpperCase(), startX + 80 * scaleFactor, row3Y);

    // Row 4: Gender (reduced from 74mm to 66mm)
    const row4Y = startY + 61 * scaleFactor;
    doc.setFont(fontFamily, "normal");
    const genderDisplay =
      data.gender.toLowerCase() === "male"
        ? "M"
        : data.gender.toLowerCase() === "female"
        ? "F"
        : data.gender.charAt(0).toUpperCase();
    doc.text(genderDisplay, startX + 80 * scaleFactor, row4Y);

    // Add user photo if available (positioned at 30mm from top, 130mm from left)
    if (data.image) {
      try {
        const photoX = startX + 174.8 * scaleFactor;
        const photoY = startY + 24.2 * scaleFactor;
        const photoWidth = 34.2 * scaleFactor;
        const photoHeight = 41 * scaleFactor;

        // Add user photo
        doc.addImage(
          data.image,
          "JPEG",
          photoX,
          photoY,
          photoWidth,
          photoHeight
        );
      } catch (error) {
        console.log("Could not process user image for PDF");
      }
    }
  } catch (error) {
    console.error("Error loading template image:", error);
    // Fallback: create a simple PDF without template
    doc.setFontSize(18);
    doc.setFont(fontFamily, "normal");
    doc.text("Regular NIN Verification Slip", 20, 30);
    doc.setFontSize(14);
    doc.setFont(fontFamily, "normal");
    doc.text(`NIN: ${data.nin}`, 20, 50);
    doc.text(
      `Name: ${data.firstName} ${data.middleName} ${data.lastName}`,
      20,
      65
    );
    doc.text(`Gender: ${data.gender}`, 20, 80);
    if (data.trackingId) {
      doc.text(`Tracking ID: ${data.trackingId}`, 20, 95);
    }
    if (data.residenceAddress) {
      doc.text(`Address: ${data.residenceAddress}`, 20, 110);
    }
  }

  // Generate filename
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `Regular_NIN_Slip_${data.nin}_${timestamp}.pdf`;

  // Save the PDF
  doc.save(filename);
};

// Helper function to load image
const loadImage = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = src;
  });
};

// Smart text wrapping function for addresses
const wrapTextSmart = (
  text: string,
  maxWidth: number,
  doc: jsPDF
): string[] => {
  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine ? `${currentLine} ${word}` : word;

    // Check if adding this word exceeds the width
    const textWidth = doc.getTextWidth(testLine);

    if (textWidth <= maxWidth) {
      // Word fits, add it to current line
      currentLine = testLine;
    } else {
      // Word doesn't fit
      if (currentLine) {
        // Current line has content, start a new line
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word is too long, break it character by character
        const brokenWord = breakLongWord(word, maxWidth, doc);
        lines.push(...brokenWord);
        currentLine = "";
      }
    }
  }

  // Add the last line if it has content
  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

// Break long words character by character
const breakLongWord = (
  word: string,
  maxWidth: number,
  doc: jsPDF
): string[] => {
  const lines: string[] = [];
  let currentLine = "";

  for (let i = 0; i < word.length; i++) {
    const char = word[i];
    const testLine = currentLine + char;
    const textWidth = doc.getTextWidth(testLine);

    if (textWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = char;
      } else {
        // Single character is too wide (shouldn't happen with normal fonts)
        lines.push(char);
        currentLine = "";
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};
